import { ViewConfig } from "@lib/views";
import { DATE_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { ResourceType } from "@common/types/auditLog";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import { LogTimeCell } from "../../cells/LogTimeCell";
import { HistoryClassDetailsColumn } from "./historyClassDetailsView";

export interface HistoryClassCreateDeleteDetailsRow extends HistoryDetailsBaseRow {
    id: string;
    resourceType: ResourceType;
    resourceId: string;
    className: string;
    previous: string;
    modified: string;
}

export interface HistoryClassUpdateDetailsRow extends HistoryDetailsBaseRow {
    id: string;
    resourceType: ResourceType;
    resourceId: string;
    className: string;
    changedFieldLabel: string | null;
    previous: string;
    modified: string;
}

const DEFAULT_PREVIOUS_LABEL = "Previous";
const DEFAULT_MODIFIED_LABEL = "Modified";

const getSingleFieldLabel = (
    data: HistoryClassUpdateDetailsRow[],
): string | null => {
    const fieldValues = data
        .map((row) => row.changedFieldLabel)
        .filter((field): field is string => field !== null);
    const uniqueFields = Array.from(new Set(fieldValues));

    if (uniqueFields.length !== 1) {
        return null;
    }

    return uniqueFields[0];
};

export const createHistoryClassCreateDeleteViewConfig = (): ViewConfig<
    HistoryClassCreateDeleteDetailsRow,
    HistoryClassDetailsColumn,
    never
> => ({
    columns: {
        [HistoryClassDetailsColumn.TIME]: {
            name: "Time",
            header: "",
            accessorKey: "logDate",
            cell: LogTimeCell,
            ...DATE_COLUMN_SIZE,
        },
        [HistoryClassDetailsColumn.CLASS_NAME]: {
            name: "Class name",
            header: "Class name",
            accessorKey: "className",
            minSize: 260,
            maxSize: 400,
        },
        [HistoryClassDetailsColumn.PREVIOUS]: {
            name: "Previous",
            header: "Previous",
            accessorKey: "previous",
            minSize: 280,
            maxSize: 420,
        },
        [HistoryClassDetailsColumn.MODIFIED]: {
            name: "Modified",
            header: "Modified",
            accessorKey: "modified",
            minSize: 280,
            maxSize: 420,
        },
    },
});

export const createHistoryClassUpdateViewConfig = (
    data: HistoryClassUpdateDetailsRow[],
): ViewConfig<
    HistoryClassUpdateDetailsRow,
    HistoryClassDetailsColumn,
    never
> => {
    const singleField = getSingleFieldLabel(data);
    const previousLabel = singleField
        ? `Previous ${singleField.toLowerCase()}`
        : DEFAULT_PREVIOUS_LABEL;
    const modifiedLabel = singleField
        ? `Modified ${singleField.toLowerCase()}`
        : DEFAULT_MODIFIED_LABEL;

    return {
        columns: {
            [HistoryClassDetailsColumn.TIME]: {
                name: "Time",
                header: "",
                accessorKey: "logDate",
                cell: LogTimeCell,
                ...DATE_COLUMN_SIZE,
            },
            [HistoryClassDetailsColumn.CLASS_NAME]: {
                name: "Class name",
                header: "Class name",
                accessorKey: "className",
                minSize: 260,
                maxSize: 400,
            },
            [HistoryClassDetailsColumn.PREVIOUS]: {
                name: previousLabel,
                header: previousLabel,
                accessorKey: "previous",
                minSize: 280,
                maxSize: 420,
            },
            [HistoryClassDetailsColumn.MODIFIED]: {
                name: modifiedLabel,
                header: modifiedLabel,
                accessorKey: "modified",
                minSize: 280,
                maxSize: 420,
            },
        },
    };
};
